import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { CaseloadReading, READING_LABEL } from '@innerhive-internal/innerhive-common';
import { IAvatarIdentity } from '../../avatar/IAvatarIdentity';
import { PersonAvatar } from '../../avatar/Avatars';
import { READING_COLOR } from './agencyChartTheme';

/** The reading-coloured ring round the photo, and the gap that keeps it off the photo's edge. */
const RING = 2;
const GAP = 1.5;

const useStyles = createUseStyles({
    ringed: {
        display: 'inline-flex',
        flexShrink: 0,
        padding: GAP,
        borderRadius: '50%',
        borderStyle: 'solid',
        borderWidth: RING,
        // Paper drops backgrounds unless asked, and a border is not one, so the ring prints as is.
        printColorAdjust: 'exact',
        '-webkit-print-color-adjust': 'exact'
    },

    // The reading for assistive tech and session replay, since no attribute is read aloud.
    hidden: {
        position: 'absolute',
        width: 1,
        height: 1,
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap' as const
    }
});

/**
 * A client's photo rimmed in the colour of their reading, wherever a list names them: the same
 * colour their cell has on the caseload map, drawn here from the same reading, so a client cannot
 * be one colour on the map and another in a list that opens from it.
 *
 * No reading (a caseload not read yet, or a client the digest does not hold) draws the photo bare
 * rather than a grey ring, which would claim the client is not enough on record to compare.
 */
export const ClientReadingAvatar: FC<{
    identity: IAvatarIdentity | undefined;
    name: string;
    reading: CaseloadReading | undefined;
    /** The photo's diameter; the ring sits outside it. */
    size: number;
}> = ({ identity, name, reading, size }) => {
    const classes = useStyles();

    if (!reading) {
        return <PersonAvatar size={size} identity={identity} name={name} />;
    }

    return (
        <span
            className={classes.ringed}
            style={{ borderColor: READING_COLOR[reading] }}
            data-id="client-reading"
            data-reading={reading}
        >
            <span className={classes.hidden}>{READING_LABEL[reading]}</span>
            <PersonAvatar size={size} identity={identity} name={name} />
        </span>
    );
};
